import { AlertTriangle, Info, ListChecks, Zap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import type { CalculationResult } from "@/types/calculator";

const cardCls = "transition-all duration-300 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-lg";
const headingCls = "text-lg font-bold text-gray-900 dark:text-white";
const fmtKw = (w: number) => `${(w / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} kW`;

// Total solution power per the Lenovo sizing rubric: compute + service nodes +
// ToR switches + storage.
export function PowerCard({ result }: { result: CalculationResult }) {
  const power = result.serverConfiguration.power;
  if (!power) return null;
  return (
    <Card className={cardCls}>
      <CardContent className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Zap className="w-5 h-5 text-lenovo-blue" />
          <h3 className={headingCls}>Solution Power</h3>
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
          <span className="text-2xl font-bold text-gray-900 dark:text-white">{power.peak_kw.toLocaleString()} kW</span>{" "}
          peak · {power.idle_kw.toLocaleString()} kW idle
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 dark:text-gray-400">
              <th className="text-left font-medium pb-2">Component</th>
              <th className="text-right font-medium pb-2">Idle</th>
              <th className="text-right font-medium pb-2">Peak</th>
            </tr>
          </thead>
          <tbody>
            {power.items.map((item) => (
              <tr key={item.label} className="border-t border-gray-100 dark:border-gray-700">
                <td className="py-2 pr-3">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{item.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{item.count} × {item.model}</div>
                </td>
                <td className="py-2 pl-3 text-right tabular-nums text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtKw(item.idle_w)}</td>
                <td className="py-2 pl-3 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtKw(item.peak_w)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {power.notes.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-gray-500 dark:text-gray-400">
            {power.notes.map((n) => <li key={n}>{n}</li>)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// Pre-quote validation checklist; warnings first.
export function ChecksCard({ result }: { result: CalculationResult }) {
  const checks = [...(result.checks ?? [])].sort((a, b) => (a.level === b.level ? 0 : a.level === "warning" ? -1 : 1));
  if (!checks.length) return null;
  return (
    <Card className={cardCls}>
      <CardContent className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <ListChecks className="w-5 h-5 text-lenovo-blue" />
          <h3 className={headingCls}>Pre-quote Checks</h3>
        </div>
        <ul className="space-y-2">
          {checks.map((c) => (
            <li key={c.text} className="flex items-start gap-2 text-sm">
              {c.level === "warning" ? (
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" aria-label="Warning" />
              ) : (
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-lenovo-blue dark:text-blue-400" aria-label="Note" />
              )}
              <span className="text-gray-700 dark:text-gray-300">{c.text}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
